/**
 * 계약 별첨 관리 — 레거시 `ruda-admin-web/pages/admin/contractAppendix.js` 포팅.
 * 기준월별 버전 목록 · 상세 모달 · 다음 버전 자동 생성 · GENERATING 폴링.
 * 조회 조건은 FilterCard + Field(상단 라벨 / 하단 컨트롤) 디자인 시스템을 사용한다.
 */
import type { ContractAppendixArtifact } from '@/entities/contract/api/contract.api';
import {
  useContractAppendixDownloadUrl,
  useContractAppendixList,
  useGenerateContractAppendix,
} from '@/entities/contract/hooks/useContractAppendix';
import {
  APPENDIX_STATUS,
  APPENDIX_STATUS_META,
  POLL_INTERVAL_MS,
  appendixRowKey,
  formatAppendixDateTime,
  formatAppendixFileSize,
  getNextAppendixVersion,
  isAppendixArtifactReady,
} from '@/pages/contracts/contractAppendixUtils';
import { formatRefYm, getCurrentRefYm } from '@/pages/fee-rates/feeRateUtils';
import { MonthPicker } from '@/shared/components/DatePicker';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { FilterRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { cn } from '@/shared/lib/utils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { Download, Eye, FileText, Loader2, Zap } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const AppendixStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const meta =
    status && status in APPENDIX_STATUS_META
      ? APPENDIX_STATUS_META[status as keyof typeof APPENDIX_STATUS_META]
      : { label: status || '알 수 없음', badgeClass: 'bg-muted text-muted-foreground' };
  return (
    <Badge variant="secondary" className={cn('font-normal', meta.badgeClass)}>
      {meta.label}
    </Badge>
  );
};

const AppendixDetailContent: React.FC<{
  artifact: ContractAppendixArtifact;
  refYm: string;
}> = ({ artifact, refYm }) => {
  const ready = isAppendixArtifactReady(artifact);
  const s3Path =
    artifact.s3Bucket && artifact.s3Key
      ? `s3://${artifact.s3Bucket}/${artifact.s3Key}`
      : '-';

  const rows: { label: string; value: React.ReactNode; span?: boolean }[] = [
    { label: '기준월', value: formatRefYm(artifact.refYm ?? refYm) },
    { label: '버전', value: `v${artifact.version ?? '-'}` },
    {
      label: '상태',
      value: <AppendixStatusBadge status={artifact.status} />,
    },
    {
      label: '다운로드 가능',
      value: ready ? (
        <Badge className="bg-emerald-50 text-ruda-success font-normal">가능</Badge>
      ) : (
        <Badge variant="secondary" className="font-normal">
          불가
        </Badge>
      ),
    },
    { label: 'Artifact ID', value: artifact.artifactId ?? '-' },
    { label: '파일 크기', value: formatAppendixFileSize(artifact.fileSizeBytes) },
    {
      label: '생성 일시',
      value: formatAppendixDateTime(artifact.generatedAt),
    },
    {
      label: '최종 수정',
      value: formatAppendixDateTime(artifact.updatedAt),
    },
    {
      label: '등록 일시',
      value: formatAppendixDateTime(artifact.createdAt),
    },
    {
      label: '체크섬 (SHA-256)',
      value: (
        <span className="break-all font-mono text-xs">
          {artifact.checksumSha256 || '-'}
        </span>
      ),
      span: true,
    },
    {
      label: 'S3 경로',
      value: <span className="break-all text-xs">{s3Path}</span>,
      span: true,
    },
  ];

  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {rows.map((row) => (
        <div
          key={row.label}
          className={cn(
            'rounded-md border bg-muted/20 px-3 py-2',
            row.span && 'sm:col-span-2',
          )}
        >
          <dt className="text-xs text-muted-foreground">{row.label}</dt>
          <dd className="mt-1 text-sm">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
};

export const ContractAppendixPage: React.FC = () => {
  const toast = useToast();
  const [refYm, setRefYm] = useState(getCurrentRefYm());
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  /** 상세 모달에 연 행 식별자 — 목록 refetch 시 최신 artifact로 자동 반영 */
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    data: artifacts = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useContractAppendixList(refYm);

  const generateAppendix = useGenerateContractAppendix();
  const downloadUrl = useContractAppendixDownloadUrl();

  const selectedArtifact = useMemo(
    () =>
      selectedRowKey
        ? (artifacts.find((item) => appendixRowKey(item) === selectedRowKey) ??
          null)
        : null,
    [artifacts, selectedRowKey],
  );

  const notFound = !isLoading && !isError && artifacts.length === 0;

  const nextVersion = useMemo(
    () => getNextAppendixVersion(artifacts),
    [artifacts],
  );

  const hasGenerating = useMemo(
    () =>
      artifacts.some((item) => item.status === APPENDIX_STATUS.GENERATING),
    [artifacts],
  );

  const busy =
    isFetching || generateAppendix.isPending || downloadUrl.isPending;

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [isError, error, toast]);

  // 기준월 변경 시 상세 모달 닫기 (레거시와 동일)
  useEffect(() => {
    setDetailOpen(false);
    setSelectedRowKey(null);
  }, [refYm]);

  // GENERATING 중이면 3초 폴링
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!hasGenerating) return;

    pollRef.current = setInterval(() => {
      void refetch();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasGenerating, refetch]);

  const handleOpenDetail = (artifact: ContractAppendixArtifact) => {
    setSelectedRowKey(appendixRowKey(artifact));
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRowKey(null);
  };

  const handleGenerate = async () => {
    if (!refYm || refYm.length !== 6) {
      toast.error('기준년월을 선택해 주세요.');
      return;
    }
    try {
      await generateAppendix.mutateAsync({ refYm, version: nextVersion });
      await refetch();
      toast.success(`별첨 PDF v${nextVersion} 생성을 요청했습니다.`);
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setGenerateConfirmOpen(false);
    }
  };

  const handleDownload = async (artifact: ContractAppendixArtifact | null) => {
    if (!refYm || !artifact?.version) {
      toast.error('다운로드할 버전을 선택해 주세요.');
      return;
    }
    try {
      const result = await downloadUrl.mutateAsync({
        refYm,
        version: artifact.version,
      });
      if (!result?.downloadUrl) {
        toast.error('다운로드 URL을 받지 못했습니다.');
        return;
      }
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const selectedReady = isAppendixArtifactReady(selectedArtifact);
  const isGenerating = generateAppendix.isPending;
  const emptyDescription = notFound || artifacts.length === 0
    ? `${formatRefYm(refYm)} 별첨 문서가 없습니다. PDF 자동 생성을 실행해 주세요.`
    : '조회 조건을 선택한 뒤 조회하거나, PDF 자동 생성을 실행해 주세요.';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">계약 별첨 관리</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          월별 계약 별첨 PDF 버전을 조회하고, 새 버전을 생성하거나 다운로드할 수
          있습니다.
        </p>
      </div>

      <FilterCard>
        <FilterRow as="div" gap={12}>
          <Field label="기준년월" htmlFor="refYm">
            <MonthPicker
              id="refYm"
              value={refYm}
              onChange={setRefYm}
              placeholder="년월 선택"
              className="w-44"
            />
          </Field>
        </FilterRow>
      </FilterCard>

      {hasGenerating ? (
        <div
          role="status"
          className="rounded-lg border border-ruda-primary/30 bg-ruda-bg-tertiary px-4 py-3 text-sm"
        >
          <p className="font-medium text-ruda-primary">
            별첨 PDF를 생성하고 있습니다.
          </p>
          <p className="mt-1 text-muted-foreground">
            생성이 완료되면 목록이 자동으로 갱신됩니다.
          </p>
        </div>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5" />
            별첨 문서 목록
            {artifacts.length > 0 ? (
              <Badge variant="secondary" className="font-normal">
                {artifacts.length}건
              </Badge>
            ) : null}
          </CardTitle>
          <MenuWriteGate>
            <Button
              type="button"
              className="ruda-button"
              disabled={busy || hasGenerating}
              onClick={() => setGenerateConfirmOpen(true)}
            >
              <Zap className="mr-2 h-4 w-4" />
              PDF 자동 생성 (v{nextVersion})
            </Button>
          </MenuWriteGate>
        </CardHeader>
        <CardContent>
          {isLoading && artifacts.length === 0 ? (
            <LoadingSpinner tip="별첨 문서를 불러오는 중..." />
          ) : artifacts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="min-w-max">
                <TableHeader>
                  <TableRow>
                    <TableHead>버전</TableHead>
                    <TableHead>상태</TableHead>
                    <TableHead>파일 크기</TableHead>
                    <TableHead>생성 일시</TableHead>
                    <TableHead>최종 수정</TableHead>
                    <TableHead>상세</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {artifacts.map((artifact) => (
                    <TableRow
                      key={appendixRowKey(artifact)}
                      className="cursor-pointer"
                      onClick={() => handleOpenDetail(artifact)}
                    >
                      <TableCell>v{artifact.version ?? '-'}</TableCell>
                      <TableCell>
                        <AppendixStatusBadge status={artifact.status} />
                      </TableCell>
                      <TableCell>
                        {formatAppendixFileSize(artifact.fileSizeBytes)}
                      </TableCell>
                      <TableCell>
                        {formatAppendixDateTime(artifact.generatedAt)}
                      </TableCell>
                      <TableCell>
                        {formatAppendixDateTime(artifact.updatedAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto px-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetail(artifact);
                          }}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          보기
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {emptyDescription}
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={detailOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseDetail();
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {selectedArtifact
                ? `별첨 문서 상세 (v${selectedArtifact.version})`
                : '별첨 문서 상세'}
            </DialogTitle>
          </DialogHeader>
          {selectedArtifact ? (
            <AppendixDetailContent artifact={selectedArtifact} refYm={refYm} />
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseDetail}>
              닫기
            </Button>
            <Button
              type="button"
              className="ruda-button"
              disabled={!selectedReady || busy}
              onClick={() => void handleDownload(selectedArtifact)}
            >
              <Download className="mr-2 h-4 w-4" />
              PDF 다운로드
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={generateConfirmOpen}
        onOpenChange={(open) => {
          // 요청 중에는 오버레이 클릭·Esc로 닫히지 않게 한다
          if (!open && isGenerating) return;
          setGenerateConfirmOpen(open);
        }}
      >
        <AlertDialogContent aria-busy={isGenerating}>
          <AlertDialogHeader>
            <AlertDialogTitle>별첨 PDF 자동 생성</AlertDialogTitle>
            <AlertDialogDescription>
              {isGenerating
                ? `${formatRefYm(refYm)} v${nextVersion} 별첨 PDF 생성을 요청하고 있습니다. 완료될 때까지 이 창을 유지합니다.`
                : `${formatRefYm(refYm)} v${nextVersion} 별첨 PDF를 생성하시겠습니까?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isGenerating}>취소</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || hasGenerating}
              onClick={(e) => {
                e.preventDefault();
                void handleGenerate();
              }}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  생성 중...
                </>
              ) : (
                '생성'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
